import { useEffect, useRef, useState } from "react";
import RecordRTC from "recordrtc";
import { useLang } from "../OneHealthContext";
import { PULMO_COUGH_MS, PULMO_VOWEL_MS } from "../config";
import { pulmoCall } from "../careplix";
import { decorate, iconPath } from "../report/pulmoIcons";

const asset = (p) => `${process.env.PUBLIC_URL || ""}${p}`;

/* PULMO (lung) SCREEN — real CarePlix Pulmo flow.
   Records a cough and a held vowel-A (44.1kHz mono 16-bit WAV via RecordRTC),
   uploads both to CarePlix (upload-scan-audio, same scan_token as the face
   create-token), fetches the follow-up survey, submits the answers and returns
   a real Pulmo screening score. No score is ever fabricated: any failure shows
   a retry prompt. The microphone only works when the app is the top-level
   document (its own tab) — inside an embedded iframe it shows an open-in-tab
   prompt. */

const isEmbedded = (() => {
  try { return window.self !== window.top; } catch (_) { return true; }
})();

/* CarePlix survey/score responses are not strictly typed here, so normalise
   defensively across the common shapes rather than assuming one layout. */
// Map one raw CarePlix question into { id, text, options, surveyId, raw }.
// answer_options is a plain { "1": "Very Often", ... } map; the option VALUE is
// that key (which reward_dict maps to a score on the backend).
function mapQuestion(q, surveyId) {
  const id = q.question_id ?? q.id ?? q._id ?? q.qid;
  if (id == null) return null;
  const text = q.question || q.question_text || q.text || q.title || q.name || "";
  let options = [];
  const ao = q.answer_options;
  if (ao && typeof ao === "object" && !Array.isArray(ao)) {
    options = Object.keys(ao).map((k) => ({ value: String(k), label: String(ao[k]) }));
  } else {
    const rawOpts = q.options || q.answers || q.choices || q.scale;
    if (Array.isArray(rawOpts) && rawOpts.length) {
      options = rawOpts.map((o) =>
        o && typeof o === "object"
          ? { value: String(o.value ?? o.option_id ?? o.id ?? o.score ?? o.key ?? o.label), label: String(o.label ?? o.text ?? o.name ?? o.option ?? o.value) }
          : { value: String(o), label: String(o) }
      );
    }
  }
  if (!options.length) options = [1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) }));
  return { id: String(id), text: String(text), options, surveyId, raw: q };
}

function normalizeSurvey(data) {
  // Real CarePlix shape: { statusCode, response: [ { survey_id,
  // survey_question_details: [ { question_id, question, answer_options } ] } ] }
  const surveys =
    (Array.isArray(data?.response) && data.response) ||
    (Array.isArray(data?.data?.response) && data.data.response) ||
    null;
  if (surveys) {
    const out = [];
    surveys.forEach((s) => {
      const qs = s.survey_question_details || s.questions || s.survey_questions || [];
      if (Array.isArray(qs)) qs.forEach((q) => { const m = mapQuestion(q, s.survey_id); if (m) out.push(m); });
    });
    if (out.length) return out;
  }
  // Legacy / defensive fallbacks for other possible shapes.
  const d = data?.data ?? data;
  const arr =
    data?.questions || data?.survey || data?.survey_questions || data?.pulmo_survey ||
    d?.questions || d?.survey || d?.survey_questions || d?.list || d?.rows || d?.result ||
    (Array.isArray(d) ? d : null) ||
    (Array.isArray(data) ? data : null) || [];
  if (!Array.isArray(arr)) return [];
  return arr.map((q) => mapQuestion(q, q.survey_id)).filter(Boolean);
}

function readScore(data) {
  const s =
    data?.score ?? data?.pulmo_score ?? data?.total_score ??
    data?.scores?.total ?? data?.result?.score ?? data?.data?.score;
  const n = Number(s);
  return isNaN(n) ? null : Math.round(n);
}

// Defensively read the spirometry-style extras CarePlix may return alongside the
// Pulmo score. Real values only — if a field isn't present we return null and the
// Result screen shows it as unavailable (never fabricated).
// The response nesting varies, so as a last resort we deep-search the whole
// response for a key matching the field (bounded depth, primitives only).
function deepPick(root, keyRx, maxDepth = 6) {
  const seen = new Set();
  const walk = (o, d) => {
    if (!o || typeof o !== "object" || d > maxDepth || seen.has(o)) return undefined;
    seen.add(o);
    for (const [k, v] of Object.entries(o)) {
      if (keyRx.test(k) && v != null && v !== "" && typeof v !== "object") return v;
    }
    for (const v of Object.values(o)) {
      const r = walk(v, d + 1);
      if (r !== undefined) return r;
    }
    return undefined;
  };
  const r = walk(root, 0);
  return r === undefined ? null : r;
}

function readPulmoDetails(data) {
  const d = data?.result ?? data?.data ?? data ?? {};
  const src = { ...data, ...d };
  const pick = (...keys) => {
    for (const k of keys) {
      const v = src[k] ?? data?.[k] ?? d?.[k];
      if (v != null && v !== "") return v;
    }
    return null;
  };
  const ratioRaw =
    pick("fev1_fvc", "fev1fvc", "fev_fvc", "fev1_fvc_ratio", "fvc_ratio", "ratio") ??
    deepPick(data, /fev.?1?.?fvc|fvc.?ratio/i);
  let fevFvc = ratioRaw == null ? null : Number(ratioRaw);
  if (fevFvc != null && !isNaN(fevFvc)) {
    if (fevFvc > 1.5) fevFvc = fevFvc / 100; // normalise a percentage to a 0–1 ratio
    fevFvc = Math.round(fevFvc * 100) / 100;
  } else fevFvc = null;

  const oadRaw =
    pick("oad", "oad_risk", "oad_flag", "oad_status", "obstructive_airway_disease", "airway_obstruction") ??
    deepPick(data, /(^|_)oad|obstruct/i);
  let oad = null;
  if (typeof oadRaw === "boolean") oad = oadRaw;
  else if (typeof oadRaw === "number") oad = oadRaw > 0;
  else if (typeof oadRaw === "string") {
    const t = oadRaw.trim().toLowerCase();
    if (["true", "yes", "1", "positive", "detected", "high", "elevated"].includes(t)) oad = true;
    else if (["false", "no", "0", "negative", "none", "low", "normal", "not detected"].includes(t)) oad = false;
  }
  return { fevFvc, oad };
}

/* ── Pre-answering from the worker record ──────────────────
   The survey asks for age, which the kiosk already knows — the worker gave it
   at identification (or it came from their record). Asking again is friction and
   invites a different answer to the one attached to the scan.

   Age options come back in shapes like "18-30", "31-45", "Under 20", "60+", so
   we parse whichever band contains the known age and pre-select it. If nothing
   parses, the question is left for the worker rather than guessed. */
const AGE_Q = /\bage\b|\bumur\b|\bedad\b/i;

function optionMatchesAge(label, age) {
  const t = String(label).toLowerCase().replace(/\s+/g, " ").trim();

  // "18-30", "18 to 30", "18 – 30"
  let m = t.match(/(\d{1,3})\s*(?:-|–|to)\s*(\d{1,3})/);
  if (m) return age >= +m[1] && age <= +m[2];

  // "60+", "over 60", "above 60", "60 and above"
  m = t.match(/(?:over|above|more than|greater than)\s*(\d{1,3})/) ||
      t.match(/(\d{1,3})\s*(?:\+|and above|or above|and over)/);
  if (m) return age > +m[1] - (t.includes("and above") || t.includes("or above") ? 1 : 0);

  // "under 20", "below 20", "less than 20"
  m = t.match(/(?:under|below|less than)\s*(\d{1,3})/);
  if (m) return age < +m[1];

  // a bare number: exact match only
  m = t.match(/^(\d{1,3})$/);
  if (m) return age === +m[1];

  return false;
}

/** @returns {Record<string,string>} answers to pre-fill */
export function prefillAnswers(questions, employee) {
  const out = {};
  const age = Number(employee?.age);
  if (!(age > 0)) return out;

  for (const q of questions) {
    if (!AGE_Q.test(q.text || "")) continue;
    const hit = (q.options || []).find((o) => optionMatchesAge(o.label, age));
    if (hit) out[q.id] = hit.value;
  }
  return out;
}

export default function Pulmo({ scanToken, employee, onDone, coughOnly = false }) {
  const { tr } = useLang();
  const [qIdx, setQIdx] = useState(0);
  const advanceRef = useRef(null); // pending auto-advance timer — cancellable
  // intro · cough · vowel · uploading · survey · scoring · done · error
  const [stage, setStage] = useState(isEmbedded ? "embed" : "intro");
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(null);
  const [details, setDetails] = useState({ fevFvc: null, oad: null });
  const [errMsg, setErrMsg] = useState(null);
  const [retryStage, setRetryStage] = useState(null); // where "Try Again" resumes

  const streamRef = useRef(null);
  const coughRef = useRef(null);
  const vowelRef = useRef(null);
  const tickRef = useRef(null);
  const stopTimer = useRef(null);
  const lastQaRef = useRef([]); // survey answers last submitted (for retry)

  useEffect(() => () => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (stopTimer.current) clearTimeout(stopTimer.current);
    if (advanceRef.current) clearTimeout(advanceRef.current);
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
  }, []);

  const getStream = async () => {
    if (streamRef.current) return streamRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: { ideal: 1 },
        sampleRate: { ideal: 44100, min: 16000, max: 48000 },
        echoCancellation: { ideal: true },
        noiseSuppression: { ideal: false },
      },
    });
    streamRef.current = stream;
    return stream;
  };

  const fail = (msg, resumeStage) => {
    setErrMsg(msg); setRetryStage(resumeStage); setStage("error");
  };

  // Record for a fixed duration and resolve the base64 WAV data URL.
  const recordFor = (stream, ms) =>
    new Promise((resolve, reject) => {
      let rec;
      try {
        rec = new RecordRTC(stream, {
          type: "audio",
          mimeType: "audio/wav",
          recorderType: RecordRTC.StereoAudioRecorder,
          numberOfAudioChannels: 1,
          desiredSampRate: 44100,
        });
        rec.startRecording();
      } catch (e) { reject(e); return; }
      setRecording(true);
      setRecSecs(Math.round(ms / 1000));
      tickRef.current = setInterval(() => setRecSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
      stopTimer.current = setTimeout(() => {
        if (tickRef.current) clearInterval(tickRef.current);
        rec.stopRecording(() => {
          setRecording(false);
          try { rec.getDataURL((dataURL) => resolve(dataURL)); }
          catch (e) { reject(e); }
        });
      }, ms);
    });

  const startCough = async () => {
    try {
      const stream = await getStream();
      const b64 = await recordFor(stream, PULMO_COUGH_MS);
      coughRef.current = b64;
      if (coughOnly) {
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
        streamRef.current = null;
        upload();
      } else {
        setStage("vowel");
      }
    } catch (e) {
      console.warn("cough record failed", e?.message);
      setRecording(false);
      fail(tr("pulmo.micDenied"), "cough");
    }
  };

  const startVowel = async () => {
    try {
      const stream = await getStream();
      const b64 = await recordFor(stream, PULMO_VOWEL_MS);
      vowelRef.current = b64;
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
      streamRef.current = null;
      upload();
    } catch (e) {
      console.warn("vowel record failed", e?.message);
      setRecording(false);
      fail(tr("pulmo.micDenied"), "vowel");
    }
  };

  const upload = async () => {
    setStage("uploading"); setErrMsg(null);
    try {
      const body = {
        scan_token: scanToken,
        cough_base64: coughRef.current,
      };
      if (!coughOnly) body.vowel_a_base64 = vowelRef.current;
      const { ok } = await pulmoCall("upload-scan-audio", body, 45000);
      if (!ok) throw new Error("upload failed");
      if (coughOnly) setStage("done");
      else await loadSurvey();
    } catch (e) {
      console.error("upload-scan-audio failed", e?.message);
      fail(tr("pulmo.errUpload"), "upload");
    }
  };

  const loadSurvey = async () => {
    try {
      const { ok, data } = await pulmoCall("fetch-survey", {
        scan_token: scanToken,
        table_config_rows_per_page: 50,
        table_config_page_no: 1,
      }, 20000);
      if (!ok) { fail(tr("pulmo.errSurvey"), "survey"); return; }
      console.log("CarePlix fetch-survey →", data);
      const qs = normalizeSurvey(data);
      // Never auto-score. Scoring must only run after the worker answers the
      // questions and submits manually. If we can't parse any questions from a
      // 200 response, surface a retry rather than silently calling scores.
      if (qs.length) {
        if (advanceRef.current) { clearTimeout(advanceRef.current); advanceRef.current = null; }
        setQuestions(qs); setAnswers(prefillAnswers(qs, employee)); setQIdx(0); setStage("survey");
      }
      else { fail(tr("pulmo.errSurvey"), "survey"); }
    } catch (e) {
      console.error("fetch-survey failed", e?.message);
      fail(tr("pulmo.errSurvey"), "survey");
    }
  };

  const computeScore = async (qa) => {
    lastQaRef.current = qa;
    setStage("scoring"); setErrMsg(null);
    try {
      console.log("CarePlix fetch-answer-scores ←", qa);
      const { ok, data } = await pulmoCall("fetch-answer-scores", {
        scan_token: scanToken,
        questions_answers: qa,
        return_scores: true,
      }, 20000);
      console.log("CarePlix fetch-answer-scores →", data);
      // CarePlix 501 "Could not generate Pulmo Score" → the recordings need to
      // be captured again. Clear both audio files and restart at the cough step.
      if (Number(data?.statusCode) === 501 || /could not generate/i.test(String(data?.message || ""))) {
        coughRef.current = null; vowelRef.current = null;
        fail(tr("pulmo.errScoreAudio"), "recapture");
        return;
      }
      if (!ok) throw new Error("score failed");
      const s = readScore(data);
      if (s == null) throw new Error("no score"); // never advance without a real score
      setScore(s);
      setDetails(readPulmoDetails(data));
      setStage("done");
    } catch (e) {
      console.error("fetch-answer-scores failed", e?.message);
      fail(tr("pulmo.errScore"), "score");
    }
  };

  // fetch-answer-scores expects questions_answers as a { question_id: answerKey }
  // OBJECT MAP (per the CarePlix API docs), where answerKey is the selected
  // answer_options key, e.g. "3". This is exactly how `answers` is keyed.
  const submitSurvey = () => {
    const qa = {};
    questions.forEach((q) => { if (answers[q.id] != null) qa[q.id] = String(answers[q.id]); });
    computeScore(qa);
  };

  const retry = () => {
    setErrMsg(null);
    if (retryStage === "cough") setStage("cough");
    else if (retryStage === "vowel") setStage("vowel");
    else if (retryStage === "upload") upload();
    else if (retryStage === "survey") loadSurvey();
    else if (retryStage === "score") computeScore(lastQaRef.current);
    else if (retryStage === "recapture") setStage("cough"); // re-record BOTH audio files
    else setStage("intro");
  };

  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id] != null);

  /* ── Renders ── */
  const Head = ({ title, sub }) => (
    <>
      <div className="oh-kicker">{tr("pulmo.kicker")}</div>
      <div style={{ height: 8 }} />
      <h2 className="oh-h2">{title}</h2>
      {sub ? (<><div style={{ height: 6 }} /><p className="oh-sub" style={{ maxWidth: 340 }}>{sub}</p></>) : null}
    </>
  );

  if (stage === "embed") {
    return (
      <div className="oh-view" style={{ justifyContent: "center", alignItems: "center", textAlign: "center" }}>
        <div className="em" style={{ fontSize: 40 }}>🎙️</div>
        <div style={{ height: 12 }} />
        <h2 className="oh-h2">{tr("pulmo.needTab")}</h2>
        <div style={{ height: 8 }} />
        <p className="oh-sub" style={{ maxWidth: 340 }}>{tr("pulmo.needTabMsg")}</p>
        <div className="oh-spacer" />
        <button className="oh-btn" style={{ maxWidth: 360 }} onClick={() => window.open(window.location.href, "_blank", "noopener")}>{tr("scan.openTab")}</button>
      </div>
    );
  }

  if (stage === "error") {
    return (
      <div className="oh-view" style={{ justifyContent: "center", alignItems: "center", textAlign: "center" }}>
        <div className="em" style={{ fontSize: 40 }}>⚠️</div>
        <div style={{ height: 12 }} />
        <p className="oh-sub" style={{ maxWidth: 340 }}>{errMsg}</p>
        <div className="oh-spacer" />
        <button className="oh-btn" style={{ maxWidth: 360 }} onClick={retry}>{tr("common.tryAgain")}</button>
      </div>
    );
  }

  if (stage === "intro") {
    return (
      <div className="oh-view" style={{ justifyContent: "center", alignItems: "center", textAlign: "center" }}>
        <Head title={tr("pulmo.title")} sub={tr(coughOnly ? "pulmo.coughOnlySub" : "pulmo.sub")} />
        <div className="oh-spacer" />
        <button className="oh-btn" style={{ maxWidth: 360 }} onClick={() => setStage("cough")}>{tr("common.continue")}</button>
      </div>
    );
  }

  if (stage === "cough" || stage === "vowel") {
    const isCough = stage === "cough";
    return (
      <div className="oh-view" style={{ justifyContent: "center", alignItems: "center", textAlign: "center" }}>
        <Head title={isCough ? tr("pulmo.step1Title") : tr("pulmo.step2Title")} sub={isCough ? tr("pulmo.step1Hint") : tr("pulmo.step2Hint")} />
        <div style={{ height: 26 }} />
        <div className={"oh-recorder" + (recording ? " on" : "")}>
          <div className="oh-recorder-ring">{recording ? recSecs : (isCough ? "🫁" : "🗣️")}</div>
        </div>
        <div style={{ height: 26 }} />
        <button
          className="oh-btn"
          style={{ maxWidth: 360 }}
          disabled={recording}
          onClick={isCough ? startCough : startVowel}
        >
          {recording ? tr("pulmo.recording") : isCough ? tr("pulmo.startCough") : tr("pulmo.startVowel")}
        </button>
        <div className="oh-spacer" />
      </div>
    );
  }

  if (stage === "uploading" || stage === "scoring") {
    return (
      <div className="oh-view" style={{ justifyContent: "center", alignItems: "center", textAlign: "center" }}>
        <div className="oh-spin" />
        <div style={{ height: 16 }} />
        <p className="oh-sub">{stage === "uploading" ? tr("pulmo.uploading") : tr("pulmo.scoring")}</p>
      </div>
    );
  }

  if (stage === "survey") {
    return (
      <div className="oh-view">
        <div className="oh-kicker">{tr("pulmo.kicker")}</div>
        <div style={{ height: 6 }} />
        <h2 className="oh-h2">{tr("pulmo.surveyTitle")}</h2>
        <div style={{ height: 4 }} />
        <p className="oh-sub">{tr("pulmo.surveySub")}</p>
        {/* ONE QUESTION AT A TIME.
            A wall of clinical questions on a kiosk gets tapped through at random,
            and those answers feed the lung score. A single large icon and one
            short question is answerable without reading much English. */}
        {(() => {
          const q = questions[qIdx];
          if (!q) return null;
          const d = decorate(q);
          const chosen = answers[q.id];
          return (
            <div className="oh-q1">
              <div className="oh-q1-count">
                {qIdx + 1} / {questions.length}
              </div>

              {/* Supplied artwork where we have it; the line icon otherwise. */}
              {["cough", "stairs", "smoke", "wheeze"].includes(d.icon) ? (
                <img className="oh-q1-img" src={asset(`/icons/pulmo/${d.icon}.png`)} alt="" />
              ) : (
                <svg className="oh-q1-ico" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="1.6"
                     strokeLinecap="round" strokeLinejoin="round"
                     dangerouslySetInnerHTML={{ __html: iconPath(d.icon) }} />
              )}

              <div className="oh-q1-text">{d.plain || q.text}</div>
              {d.plain && <div className="oh-q1-sub">{q.text}</div>}

              <div className="oh-q1-opts">
                {d.options.map((o) => (
                  <button
                    type="button"
                    key={o.value}
                    className={"oh-q1-opt tone-" + o.tone + (chosen === o.value ? " on" : "")}
                    onClick={() => {
                      setAnswers({ ...answers, [q.id]: o.value });
                      // Advance automatically: one tap per question, no Next.
                      // Tracked so Back (or a second tap) cancels a pending advance.
                      if (advanceRef.current) clearTimeout(advanceRef.current);
                      if (qIdx < questions.length - 1) {
                        advanceRef.current = setTimeout(() => {
                          advanceRef.current = null;
                          setQIdx((i) => Math.min(questions.length - 1, i + 1));
                        }, 180);
                      }
                    }}
                  >
                    {o.plain}
                  </button>
                ))}
              </div>

              {/* Answering advances automatically, so Continue would be dead
                  weight — only Back is needed, set apart from the options so it
                  is not tapped by mistake. */}
              <div className="oh-q1-nav">
                <button className="oh-btn ghost sm" disabled={qIdx === 0}
                  onClick={() => {
                    if (advanceRef.current) { clearTimeout(advanceRef.current); advanceRef.current = null; }
                    setQIdx((i) => Math.max(0, i - 1));
                  }}>
                  ‹ {tr("common.back")}
                </button>
              </div>
            </div>
          );
        })()}
        {/* Submit appears only on the last question — offering it earlier invites
            a half-answered survey, and every unanswered question still feeds the
            score. */}
        {qIdx === questions.length - 1 && (
          <div className="oh-scan-actions single">
            <button className="oh-btn" disabled={!allAnswered} onClick={submitSurvey}>
              {tr("pulmo.submit")}
            </button>
          </div>
        )}
      </div>
    );
  }

  // done
  return (
    <div className="oh-view" style={{ justifyContent: "center", alignItems: "center", textAlign: "center" }}>
      <div className="oh-mark clear lg" />
      <div style={{ height: 22 }} />
      <h2 className="oh-h2">{tr("pulmo.complete")}</h2>
      <div style={{ height: 8 }} />
      <p className="oh-sub" style={{ maxWidth: 340 }}>{tr(coughOnly ? "pulmo.coughOnlyCompleteSub" : "pulmo.completeSub")}</p>
      <div className="oh-spacer" />
      <button className="oh-btn" style={{ maxWidth: 360 }} onClick={() => onDone(coughOnly
        ? { completed: true, mode: "cough_only", score: null, cough: !!coughRef.current, vowel: false }
        : { score, fevFvc: details.fevFvc, oad: details.oad, cough: !!coughRef.current, vowel: !!vowelRef.current }
      )}>{tr("common.continue")}</button>
    </div>
  );
}
